import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { LeadCard } from "@/components/LeadCard";
import { LeadForm } from "@/components/LeadForm";
import { LeadDetail } from "@/components/LeadDetail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Building2, X } from "lucide-react";

const STAGES = [
  { key: "new", label: "New", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  { key: "contacted", label: "Contacted", color: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" },
  { key: "qualified", label: "Qualified", color: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  { key: "proposal", label: "Proposal", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  { key: "negotiation", label: "Negotiation", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  { key: "won", label: "Won", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  { key: "lost", label: "Lost", color: "bg-red-500/10 text-red-400 border-red-500/20" },
];

const PRIORITY_OPTIONS = ["All", "high", "medium", "low"];

const gbpCompact = new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP", maximumFractionDigits: 0
});

// Skeleton placeholder for a single kanban column
const ColumnSkeleton = ({ stage }) => (
  <div className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px] snap-center">
    <div className="flex items-center justify-between mb-3 px-1">
      <Skeleton className="h-5 w-20" />
      <Skeleton className="h-4 w-12" />
    </div>
    <div className="min-h-[300px] md:min-h-[400px] rounded-sm border border-white/5 bg-muted/30 p-2 space-y-2">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-[90px] w-full rounded-sm" />
      ))}
    </div>
  </div>
);

// Empty board state
const BoardEmptyState = ({ onAddLead }) => (
  <div data-testid="pipeline-empty" className="flex flex-col items-center justify-center py-24 text-center w-full">
    <div className="w-16 h-16 rounded-sm bg-blue-500/10 flex items-center justify-center mb-5">
      <Building2 className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
    </div>
    <h2
      className="text-2xl font-bold tracking-tight uppercase mb-2"
      style={{ fontFamily: "Barlow Condensed, sans-serif" }}
    >
      No leads yet
    </h2>
    <p className="text-sm text-muted-foreground mb-6 max-w-xs">
      Add your first lead to see the pipeline
    </p>
    <Button
      data-testid="pipeline-empty-add-lead"
      onClick={onAddLead}
      className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-6"
    >
      <Plus className="w-4 h-4 mr-2" /> Add Lead
    </Button>
  </div>
);

// Filter bar
const FilterBar = ({ priorityFilter, setPriorityFilter, serviceFilter, setServiceFilter, serviceTypes, onClear }) => {
  const hasFilter = priorityFilter !== "All" || serviceFilter !== "All";
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="pipeline-filter-bar">
      {/* Priority segmented buttons */}
      <div className="flex rounded-sm border border-white/10 overflow-hidden" data-testid="priority-filter">
        {PRIORITY_OPTIONS.map((p) => (
          <button
            key={p}
            data-testid={`priority-filter-${p.toLowerCase()}`}
            onClick={() => setPriorityFilter(p)}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors duration-150 ${
              priorityFilter === p
                ? "bg-blue-500 text-white"
                : "text-muted-foreground hover:text-foreground hover:bg-white/5"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Service type dropdown */}
      <Select value={serviceFilter} onValueChange={setServiceFilter}>
        <SelectTrigger
          data-testid="service-filter"
          className="h-8 w-[200px] text-xs bg-secondary/50 border-white/10 rounded-sm"
        >
          <SelectValue placeholder="Service type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="All">All Services</SelectItem>
          {serviceTypes.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Clear filters */}
      {hasFilter && (
        <Button
          data-testid="clear-filters"
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="h-8 text-xs text-muted-foreground hover:text-foreground px-2 gap-1"
        >
          <X className="w-3 h-3" /> Clear filters
        </Button>
      )}
    </div>
  );
};

export default function PipelinePage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [defaultStage, setDefaultStage] = useState("new");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [serviceFilter, setServiceFilter] = useState("All");

  const fetchLeads = useCallback(async () => {
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStage = destination.droppableId;

    setLeads(prev => prev.map(l => l.id === draggableId ? { ...l, stage: newStage } : l));

    try {
      await api.patch(`/leads/${draggableId}/stage`, { stage: newStage });
      toast.success(`Lead moved to ${STAGES.find(s => s.key === newStage)?.label || newStage}`);
    } catch (err) {
      toast.error("Failed to update stage");
      fetchLeads();
    }
  };

  const handleCreateLead = async (data) => {
    try {
      await api.post("/leads", data);
      toast.success("Lead created");
      setShowForm(false);
      fetchLeads();
    } catch (err) {
      toast.error("Failed to create lead");
    }
  };

  const handleEditLead = async (data) => {
    try {
      await api.put(`/leads/${editLead.id}`, data);
      toast.success("Lead updated");
      setEditLead(null);
      fetchLeads();
    } catch (err) {
      toast.error("Failed to update lead");
    }
  };

  const handleCardClick = (lead) => setDetailLead(lead);

  const openAddForStage = (stageKey) => {
    setDefaultStage(stageKey);
    setShowForm(true);
  };

  const clearFilters = () => {
    setPriorityFilter("All");
    setServiceFilter("All");
  };

  // Derive unique service types for the filter dropdown
  const serviceTypes = useMemo(() => {
    const types = [...new Set(leads.map((l) => l.service_type).filter(Boolean))].sort();
    return types;
  }, [leads]);

  // Apply client-side filters
  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      if (priorityFilter !== "All" && l.priority !== priorityFilter) return false;
      if (serviceFilter !== "All" && l.service_type !== serviceFilter) return false;
      return true;
    });
  }, [leads, priorityFilter, serviceFilter]);

  const getLeadsByStage = (stage) => filteredLeads.filter(l => l.stage === stage);

  if (loading) {
    return (
      <div data-testid="pipeline-loading">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
          <div>
            <Skeleton className="h-10 w-36 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {STAGES.map((stage) => <ColumnSkeleton key={stage.key} stage={stage} />)}
        </div>
      </div>
    );
  }

  const isEmpty = leads.length === 0;

  return (
    <div data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
        <div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            Pipeline
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            Drag leads between stages to update their status
          </p>
        </div>
        <Button
          data-testid="pipeline-add-lead"
          onClick={() => { setDefaultStage("new"); setShowForm(true); }}
          className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-5"
        >
          <Plus className="w-4 h-4 mr-2" /> New Lead
        </Button>
      </div>

      {isEmpty ? (
        <BoardEmptyState onAddLead={() => { setDefaultStage("new"); setShowForm(true); }} />
      ) : (
        <>
          {/* Filter bar */}
          <FilterBar
            priorityFilter={priorityFilter}
            setPriorityFilter={setPriorityFilter}
            serviceFilter={serviceFilter}
            setServiceFilter={setServiceFilter}
            serviceTypes={serviceTypes}
            onClear={clearFilters}
          />

          {/* Kanban Board */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <div
              className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory"
              data-testid="kanban-board"
            >
              {STAGES.map((stage) => {
                const stageLeads = getLeadsByStage(stage.key);
                const stageValue = stageLeads.reduce((sum, l) => sum + (Number(l.estimated_value) || 0), 0);

                return (
                  <div
                    key={stage.key}
                    data-testid={`pipeline-column-${stage.key}`}
                    className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px] snap-center flex flex-col"
                  >
                    {/* Column header */}
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] rounded-sm ${stage.color}`}>
                          {stage.label}
                        </Badge>
                        <span
                          className="text-xs text-muted-foreground font-mono"
                          data-testid={`column-count-${stage.key}`}
                        >
                          {stageLeads.length}
                        </span>
                      </div>
                      <span
                        className="text-[10px] text-muted-foreground font-mono"
                        data-testid={`column-value-${stage.key}`}
                      >
                        {gbpCompact.format(stageValue)}
                      </span>
                    </div>

                    <Droppable droppableId={stage.key}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`pipeline-col flex-1 min-h-[300px] md:min-h-[400px] rounded-sm border border-white/5 bg-muted/30 p-2 space-y-2 ${
                            snapshot.isDraggingOver ? "pipeline-col-active border-blue-500/20" : ""
                          }`}
                        >
                          {stageLeads.map((lead, index) => (
                            <Draggable key={lead.id} draggableId={lead.id} index={index}>
                              {(provided, snapshot) => (
                                <LeadCard
                                  lead={lead}
                                  provided={provided}
                                  isDragging={snapshot.isDragging}
                                  onClick={handleCardClick}
                                  hasOverdue={false}
                                />
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {stageLeads.length === 0 && (
                            <div className="text-center py-8 text-xs text-muted-foreground/50">Drop leads here</div>
                          )}
                        </div>
                      )}
                    </Droppable>

                    {/* Quick-add button per column */}
                    <button
                      data-testid={`column-add-${stage.key}`}
                      onClick={() => openAddForStage(stage.key)}
                      className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-sm border border-white/5 text-[10px] text-muted-foreground hover:text-blue-400 hover:border-blue-500/20 hover:bg-blue-500/5 transition-colors duration-150 uppercase tracking-wider"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </>
      )}

      <LeadForm
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={handleCreateLead}
        initialData={showForm ? { stage: defaultStage } : null}
      />
      <LeadForm
        open={!!editLead}
        onOpenChange={(v) => !v && setEditLead(null)}
        onSubmit={handleEditLead}
        initialData={editLead}
      />
      <LeadDetail
        lead={detailLead}
        open={!!detailLead}
        onOpenChange={(v) => !v && setDetailLead(null)}
        onEdit={(lead) => { setDetailLead(null); setEditLead(lead); }}
        onRefresh={fetchLeads}
      />
    </div>
  );
}
